import { fetcher } from '../../../lib/api-fetcher';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

export const generalTransactionTypeSchema = z.enum(['CASH', 'JOURNAL']);
export const generalTransactionStatusSchema = z.enum(['DRAFT', 'POSTED', 'REVERSED']);
export const associationTypeSchema = z.enum([
  'BRANCH',
  'STAFF',
  'MEMBER',
  'PARTY',
]);

export const generalTransactionLineInputSchema = z.object({
  accountId: z.string().min(1, 'Account is required'),
  amountPaise: z.number().int().positive('Amount must be greater than zero'),
  direction: z.enum(['DEBIT', 'CREDIT']),
  associationType: associationTypeSchema.nullable().optional(),
  associationId: z.string().nullable().optional(),
  referenceType: z.string().max(50).nullable().optional(),
  referenceId: z.string().max(100).nullable().optional(),
  narration: z.string().nullable().optional(),
});

export type GeneralTransactionLineInput = z.infer<typeof generalTransactionLineInputSchema>;

export const createGeneralTransactionPayloadSchema = z.object({
  type: generalTransactionTypeSchema,
  entryDate: z.string(),
  particulars: z.string().min(1, 'Particulars are required').max(250, 'Particulars cannot exceed 250 characters'),
  lines: z.array(generalTransactionLineInputSchema).min(1),
  idempotencyKey: z.string().uuid(),
});

export type CreateGeneralTransactionPayload = z.infer<typeof createGeneralTransactionPayloadSchema>;

export interface GeneralTransactionListItem {
  id: string;
  orgUnitId: string | null;
  type: 'CASH' | 'JOURNAL';
  entryDate: string;
  particulars: string;
  status: 'DRAFT' | 'POSTED' | 'REVERSED';
  journalEntryId: string | null;
  createdAt: string;
}

export interface GeneralTransactionLineDetail {
  id: string;
  accountId: string;
  accountCode: string;
  accountName: string;
  amountPaise: number;
  direction: 'DEBIT' | 'CREDIT';
  associationType: string | null;
  associationId: string | null;
  referenceType: string | null;
  referenceId: string | null;
  narration: string | null;
}

export interface GeneralTransactionDetail extends GeneralTransactionListItem {
  lines: GeneralTransactionLineDetail[];
}

export interface GeneralTransactionListResponse {
  data: GeneralTransactionListItem[];
  meta: { total: number; page: number; limit: number };
}

export const generalTransactionsHooks = {
  useList: (params: { page?: number; limit?: number; type?: string; status?: string; orgUnitId?: string }) => {
    return useQuery({
      queryKey: ['general-transactions', 'list', params],
      queryFn: async () => {
        const search = new URLSearchParams();
        if (params.page) search.set('page', String(params.page));
        if (params.limit) search.set('limit', String(params.limit));
        if (params.type) search.set('type', params.type);
        if (params.status) search.set('status', params.status);
        if (params.orgUnitId) search.set('orgUnitId', params.orgUnitId);
        return fetcher.get<GeneralTransactionListResponse>(`/staff/v1/general-transactions?${search.toString()}`);
      },
    });
  },

  useDetail: (id: string | null) => {
    return useQuery({
      queryKey: ['general-transactions', 'detail', id],
      queryFn: async () => fetcher.get<GeneralTransactionDetail>(`/staff/v1/general-transactions/${id}`),
      enabled: !!id,
    });
  },

  useCreate: () => {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: async (payload: CreateGeneralTransactionPayload) => {
        return fetcher.post<{ id: string; approvalRequestId: string }>(
          '/staff/v1/general-transactions',
          payload,
          { headers: { 'Idempotency-Key': payload.idempotencyKey } },
        );
      },
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: ['general-transactions'] });
      },
    });
  },

  useReverse: (id: string) => {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: async () => {
        const idempotencyKey = crypto.randomUUID();
        return fetcher.post<{ success: boolean }>(
          `/staff/v1/general-transactions/${id}/reverse`,
          undefined,
          { headers: { 'Idempotency-Key': idempotencyKey } },
        );
      },
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: ['general-transactions'] });
      },
    });
  },
};

